import { useEffect, useRef, useState } from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { FieldLabel } from 'src/components/Field';
import { faPlus, faSearch, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Banner,
  Button,
  ButtonText,
  FormFeedback,
  SearchInput
} from '@morressier/alembic';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import StepHeader from 'src/pages/Organization/public/Journal/SubmissionUpload/components/StepHeader';
import { Layout } from 'src/components/Layout';
import { IUser } from '@publishing-engine/domain/models';
import { IInviteAdjudicatorsForm } from '../InviteExperts/commonTypes';
import ExpertsSearch from '../InviteExperts/ExpertsSearch';
import AddContactsModal from './AddContactsModal';
import { MakeReadOnly } from '../InviteExperts/AddExperts';
import { useAdjudicatorValidation } from '../../../../hooks/useAdjudicationValidtion';

interface Props {
  currentStep: number;
  totalSteps: number;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  closeModal: () => void;
  isInviteAdjudicators?: boolean;
}

const DEFAULT_EMPTY_RECORD = {
  id: '',
  name: '',
  email: '',
  externalExpertEmail: ''
};
const AddAdjudicators = ({
  currentStep,
  totalSteps,
  goToNextStep,
  closeModal
}: Props) => {
  const {
    control,
    register,
    formState: { errors },
    clearErrors,
    setValue,
    resetField,
    getValues,
    setError
  } = useFormContext<IInviteAdjudicatorsForm>();
  const [isAddContactsModalOpen, setIsAddContactsModalOpen] = useState(false);
  const { fields, append, remove, update } = useFieldArray({
    name: 'experts',
    control: control
  });
  const [isReadOnly, setIsReadOnly] = useState<MakeReadOnly>({
    readOnly: false,
    index: 0
  });
  const errorRef = useRef<HTMLDivElement | null>(null);
  const { validateOpposedReviewer, validateAdjudicators, warnings } =
    useAdjudicatorValidation(setError);

  useEffect(() => {
    validateOpposedReviewer(fields);
  }, [fields]);

  const removeReviewer = (index: number) => {
    clearErrors(`experts.${index}`);
    remove(index);

    if (fields.length === 1) {
      append(DEFAULT_EMPTY_RECORD);
    }
  };

  const handleNextStep = async () => {
    const experts = getValues('experts');
    const isValid = await validateAdjudicators(experts);

    if (isValid) {
      goToNextStep();
    } else {
      // scroll to first error
      const firstError = errorRef.current?.querySelector(
        '[id^="expert-error-"]'
      );
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };
  const onExpertsSelect = (boardMember: IUser, index: number) => {
    // update the selected org values in form
    setValue(
      `experts.${index}.name`,
      `${boardMember.firstName} ${boardMember.lastName}`,
      {
        shouldValidate: true,
        shouldDirty: true
      }
    );
    setValue(`experts.${index}.id`, boardMember.id, {
      shouldValidate: true,
      shouldDirty: true
    });
    setValue(`experts.${index}.email`, boardMember.email, {
      shouldValidate: true,
      shouldDirty: true
    });
    setIsReadOnly({ readOnly: true, index });
  };

  const handleAddContacts = (emails: string[]) => {
    // append all emails to the form
    emails.forEach(email => {
      append({
        id: '',
        name: '',
        email: '',
        externalExpertEmail: email
      });
    });

    setIsAddContactsModalOpen(false);
  };

  return (
    <>
      {isAddContactsModalOpen && (
        <AddContactsModal
          onClose={() => setIsAddContactsModalOpen(false)}
          onAddContacts={handleAddContacts}
        />
      )}
      <Layout width="960px">
        <Flex flexDirection="column" gap={8}>
          <Flex flexDirection="column">
            <StepHeader
              currentStep={currentStep}
              totalSteps={totalSteps}
              heading={'Select Adjudicators'}
              description={
                'Search for experts in the system, or invite external experts to referee this Submission’s reviews.'
              }
            />
          </Flex>

          <Flex
            gap={12}
            padding={6}
            backgroundColor="surfacePrimaryFill"
            justifyContent="space-between"
            alignItems="center"
          >
            <Flex flexDirection="column" gap={2} flex={1}>
              <Text textStyle="headingItem">
                Inviting External Adjudicators
              </Text>
              <Text textStyle="textBody">
                In case you cannot find appropriate Experts, you can add a list
                of reviewer emails to then send an invitation.
              </Text>
            </Flex>

            <Button
              variant="secondary"
              onClick={() => setIsAddContactsModalOpen(true)}
            >
              Add Contacts
            </Button>
          </Flex>
          <Flex gap={12} justifyContent="space-between" alignItems="center">
            <Flex flexDirection="column" gap={8} ref={errorRef} width="100%">
              {fields.map((field, index) => (
                <Flex
                  key={field.id}
                  gap={6}
                  padding={8}
                  flexDirection="column"
                  justifyContent="flex-end"
                  backgroundColor="surfacePrimaryFill"
                  id={
                    Object.keys(errors.experts?.[index] || {}).length > 0
                      ? `expert-error-${index}`
                      : `expert-${index}`
                  }
                >
                  {field.externalExpertEmail ? (
                    <Flex flexDirection="column" gap={2}>
                      <FieldLabel
                        label="External Expert Email"
                        subText="Provide the expert’s professional email."
                        isRequired
                      />

                      <Controller
                        name={`experts.${index}.externalExpertEmail`}
                        control={control}
                        render={({ field }) => (
                          <SearchInput
                            {...field}
                            placeholder="e.g., fabius@molecularconnections.com"
                            isInvalid={
                              !!errors.experts?.[index]?.externalExpertEmail
                            }
                            onBlur={() => field.onBlur()}
                            onChange={e => {
                              const value = (e?.target?.value || '')
                                .trim()
                                .toLowerCase();
                              field.onChange(value);
                            }}
                            onInputClear={() => {
                              resetField(field.name, { defaultValue: '' });
                              clearErrors(`experts.${index}.email`);
                            }}
                          />
                        )}
                      />

                      {errors.experts?.[index]?.email && (
                        <FormFeedback variant="error">
                          {errors.experts?.[index]?.email?.message}
                        </FormFeedback>
                      )}
                      {warnings?.[index]?.message && (
                        <Banner
                          content={warnings?.[index]?.message}
                          heading={warnings?.[index]?.title}
                          variant="attention"
                        />
                      )}
                    </Flex>
                  ) : (
                    <Flex flexDirection="column" gap={2}>
                      <FieldLabel
                        label="Search for Expert"
                        subText="Search through available experts affiliated to this Journal."
                        isRequired
                      />
                      <ExpertsSearch
                        {...register(`experts.${index}.name`)}
                        placeholder="Search for experts"
                        iconLeft={<FontAwesomeIcon icon={faSearch} />}
                        onExpertsSelect={boardMember =>
                          onExpertsSelect(boardMember, index)
                        }
                        isInvalid={Boolean(errors?.experts?.[index]?.id)}
                        onInputClear={() => {
                          update(index, DEFAULT_EMPTY_RECORD);
                          setIsReadOnly({ readOnly: false, index });
                          clearErrors(`experts.${index}.email`);
                        }}
                        isReadOnlyOption={isReadOnly}
                        index={index}
                        selectedExpert={getValues(`experts.${index}.name`)}
                      />
                      {errors.experts?.[index]?.email && (
                        <FormFeedback variant="error">
                          {errors.experts?.[index]?.email?.message}
                        </FormFeedback>
                      )}
                      {warnings?.[index]?.message && (
                        <Banner
                          content={warnings?.[index]?.message}
                          heading={warnings?.[index]?.title}
                          variant="attention"
                        />
                      )}
                    </Flex>
                  )}

                  <Flex justifyContent="flex-end">
                    <ButtonText
                      variant="secondary"
                      alignSelf="baseline"
                      iconLeft={<FontAwesomeIcon icon={faTrash} />}
                      onClick={() => {
                        removeReviewer(index);
                        setIsReadOnly({ readOnly: false, index });
                      }}
                    >
                      Delete Expert
                    </ButtonText>
                  </Flex>
                </Flex>
              ))}
            </Flex>
          </Flex>

          <Flex justifyContent="flex-end">
            <ButtonText
              alignSelf="baseline"
              iconLeft={<FontAwesomeIcon icon={faPlus} />}
              onClick={() => append(DEFAULT_EMPTY_RECORD)}
            >
              Add Another Expert
            </ButtonText>
          </Flex>
        </Flex>
      </Layout>
      <Flex
        paddingY={4}
        paddingX={12}
        bottom={0}
        zIndex={1}
        justifyContent="center"
        position="sticky"
        bgColor="surfacePrimaryFill"
        marginX="auto"
      >
        <Flex width="100%" justifyContent="space-between">
          <ButtonText variant="caution" onClick={closeModal}>
            Exit
          </ButtonText>

          <Button variant="primary" type="button" onClick={handleNextStep}>
            Next
          </Button>
        </Flex>
      </Flex>
    </>
  );
};

export default AddAdjudicators;
